/** The few product-analytics events captured from the BROWSER (posthog-js),
 *  where `lib/analytics/events.ts` — the server vocabulary behind
 *  `capturePostHogEvent` — cannot be imported (`server-only`). Kept beside it
 *  so the funnel's vocabulary still has one home per side.
 *
 *  Event NAMES live here as constants. An event with more than one call site
 *  additionally gets a typed emitter here, for the reason `events.ts` gives for
 *  its own `track*` wrappers: call sites must not hand-roll the property bag,
 *  or a key drifts across two writers. */

import posthog from "posthog-js";

import type { PremiumPaywallArm } from "@medceptor/experiments/premium-paywall";

import type { ChatTurnFailureReason } from "@/lib/chat/turn-error";
import { isPostHogCapturing } from "@/lib/posthog/is-capturing";

/** The iOS shell opened StoreKit's purchase sheet. The Apple rail's
 *  counterpart to the server-side `checkout_started` (a Stripe Checkout
 *  Session exists): distinct name because it is a client observation with
 *  no server-issued dedup id. Properties: `provider: "apple"`,
 *  `product_id`, `interval`, `from` (the paywall it came from, as in
 *  `CHECKOUT_FROM`; null for /pricing). */
export const IAP_PURCHASE_STARTED_EVENT = "iap_purchase_started";

/** We asked iOS to show its App Store rating sheet, after a student PASSED a
 *  scenario (eligibility in lib/app-review/policy.ts). Behavioural trigger
 *  only: no survey answer decides who gets asked, because selecting by
 *  sentiment is the "filtered feedback" the App Store Review Guidelines name
 *  as manipulation.
 *
 *  Counts our ASKS, not our ratings. iOS decides whether the dialog appears at
 *  all (three per device per year, plus a global opt-out) and reports back
 *  neither that decision nor the star count, so this can never be a numerator
 *  over ratings received. Shell-only — the web path has no sheet to show and
 *  emits nothing. Properties: `attempt_id` (joins an ask to its attempt, the
 *  only way to audit the gate in production, since TestFlight never shows the
 *  sheet), `scenario_slug`, `source`. */
export const APP_REVIEW_REQUESTED_EVENT = "app_review_requested";

/** How a rating request counted as {@link APP_REVIEW_REQUESTED_EVENT} ended
 *  on our side: one per ask, a few milliseconds to ten seconds after it, for
 *  the same attempt. Properties: `attempt_id`, `scenario_slug`, `outcome`
 *  (`handed_off` | `rejected` | `no_response`, see `AppReviewRequestOutcome`
 *  in lib/app-review/plugin.ts), `message` on a rejection.
 *
 *  `handed_off` means Apple's API was called, not that a sheet appeared:
 *  Apple can still suppress it and never says so. Asks minus settled events
 *  counts the reports lost to an app closed inside the wait. Added after 59
 *  asks from 2026-10-08 to 10-26 produced no new rating. */
export const APP_REVIEW_REQUEST_SETTLED_EVENT = "app_review_request_settled";

/** A student tapped "Rate Medceptor" in More and we opened the App Store
 *  review composer. Distinct from {@link APP_REVIEW_REQUESTED_EVENT}: that one
 *  is the system sheet we ask for and iOS may swallow, this one is a
 *  navigation that always happens and costs nothing from Apple's three-per-year
 *  budget. Counting them together would hide which lever actually works. */
export const APP_REVIEW_LINK_OPENED_EVENT = "app_review_link_opened";

/** A chat turn failed in a way the SERVER could not report.
 *
 *  Why not just another `reason` on the server's `chat_turn_failed`: that event
 *  fires from the route's `onFinish` / `onError`, so it only describes failures
 *  the route LIVES to see. A turn that exceeds `maxDuration` is killed by the
 *  platform — neither callback runs, nothing throws, and the turn emits
 *  nothing. The browser is the only party left that knows the student saw a
 *  failure. On 2026-09-14 that gap ran 1h47m: 104 `/api/chat` requests 504'd,
 *  22 users lost a median 82% of their turns, and not one first-party event
 *  fired — it was found in HTTP logs and a support email. (Same class as the
 *  outage in CLAUDE.md "Things that will bite" #39's prevention-layer-3
 *  paragraph, 2026-06-15: caught by a user, not by monitoring.)
 *
 *  So: this is the AVAILABILITY signal, and `chat_turn_failed` stays the
 *  QUALITY one. A turn the server survives can emit BOTH — neither carries a
 *  turn id, so the overlap is unmeasurable and the two must never be summed.
 *
 *  Two caveats for whoever sets the threshold. The denominator is
 *  consent-gated (`isPostHogCapturing`) and counts only users who happened to
 *  be online, so a quiet hour may not clear a count threshold; and
 *  `transport_error` includes the student's own dropped wifi, so the baseline
 *  is partly set outside our infrastructure. An edge 504 count has neither
 *  property and is the stronger primary detector — this is the corroborator
 *  that says users were actually harmed.
 *
 *  PARTIALLY RETIRED: a turn that exceeds `TURN_BUDGET_MS` now completes
 *  server-side and reports itself as `chat_turn_failed` reason
 *  `turn_budget_exceeded`, with served model and step count the client cannot
 *  see. This event's remaining unique coverage is "the platform killed us
 *  anyway" — still real, because the budget bounds the LOOP and a single hung
 *  step is unbounded (#1713). Revisit the threshold once there is prod data on
 *  how often each fires. */
export const CHAT_TURN_FAILED_CLIENT_EVENT = "chat_turn_failed_client";

/** Which chat-class surface failed. Every one of these POSTs a route with
 *  `maxDuration = 60`, so every one is blind to the same hang. Carried from the
 *  first emit rather than added later: a retrofit would force every alert query
 *  to carry an "or `surface` is not set" clause forever, because rows already
 *  written would lack it. */
export type ChatFailureSurface =
  | "scenario"
  | "roast"
  /** Not wired yet — neither surface reads `error` from its hook today. */
  | "qbank_help"
  | "preceptor_bot";

/** Emit {@link CHAT_TURN_FAILED_CLIENT_EVENT}. Owns the property bag so the
 *  four surfaces cannot spell a key differently, and owns the exclusions so no
 *  call site can forget them: a scenario reaching its length cap
 *  (`token_budget_exhausted`) and a session that ended elsewhere
 *  (`session_ended`) are not outages, and counting them would give the alert
 *  a baseline of healthy attempts. */
export function trackChatTurnFailedClient(p: {
  surface: ChatFailureSurface;
  reason: ChatTurnFailureReason;
  /** Absent on surfaces with no attempt (qbank help, preceptor bot). */
  attemptId?: string;
  nremtSkill?: string;
  mode?: string;
}): void {
  if (p.reason === "token_budget_exhausted" || p.reason === "session_ended") {
    return;
  }
  if (!isPostHogCapturing()) return;
  posthog.capture(CHAT_TURN_FAILED_CLIENT_EVENT, {
    surface: p.surface,
    reason: p.reason,
    attempt_id: p.attemptId ?? null,
    nremt_skill: p.nremtSkill ?? null,
    mode: p.mode ?? null,
  });
}

/** A step of the guided onboarding rendered. Fired from ONE place, the
 *  wizard chrome (`app/onboarding/_components/wizard.tsx`), so a step
 *  component can't forget it or spell a key differently. Fires before
 *  sign-in too: posthog-js gives the anonymous visitor a distinct id, and
 *  the later identify merges these into the account, which is what makes the
 *  pre-sign-up drop-off visible at all. The server's `signup_completed` stays
 *  the conversion event; this is the funnel leading to it.
 *  Properties: `step`, `audience`, `profession`, `track`, `authed`. */
const ONBOARDING_STEP_VIEWED_EVENT = "onboarding_step_viewed";

export function trackOnboardingStepViewed(p: {
  step: string;
  audience: string | null;
  profession: string | null;
  track: string | null;
  authed: boolean;
}): void {
  if (!isPostHogCapturing()) return;
  posthog.capture(ONBOARDING_STEP_VIEWED_EVENT, {
    step: p.step,
    audience: p.audience,
    profession: p.profession,
    track: p.track,
    authed: p.authed,
  });
}

/** Paywall funnel view count. Browser-side: an RSC render also runs on
 *  prefetch, which is not a view. */
const PAYWALL_VIEWED_EVENT = "paywall_viewed";

/** `web_only`: the app's card for a premium-arm viewer, not an exposure of either grid. */
export type PaywallViewedArm = PremiumPaywallArm | "web_only";

export function trackPaywallViewed(p: {
  arm: PaywallViewedArm;
  rail: "web" | "shell";
  checkoutFrom: string | null;
  cert: string;
}): void {
  if (!isPostHogCapturing()) return;
  posthog.capture(PAYWALL_VIEWED_EVENT, {
    premium_paywall_arm: p.arm,
    rail: p.rail,
    checkout_from: p.checkoutFrom,
    cert: p.cert,
  });
}

/** Where Medi appeared (`medi_shown`) or was closed with Escape
 *  (`medi_dismissed`). */
type MediSurface =
  | "welcome" // pop-up on the onboarding's first-scenario step
  | "spotlight" // ringing Start scenario
  | "call" // the guided call's dock
  | "call_done" // every beat answered
  | "results" // the tutorial's Results
  | "graded_results" // the graded run's Results
  | "lung_sounds" // his tip in the lung-sounds quiz
  | "practice"; // Practice's card

export const MEDI_PRACTICE_ACTIONS = [
  "resume",
  "start_over",
  "graded_run",
] as const;
/** `soft`: paywall #2 on graded Results; `hard` replaces /pricing. */
export type MediPaywallKind = "soft" | "hard";
/** Leaving a Medi paywall without buying: `close` is Escape on the soft one. */
export const MEDI_PAYWALL_ACTIONS = ["free", "back", "close"] as const;

/** The Medi tour's events and their properties. `beat` is 1-based.
 *  Subscribing from a Medi paywall is `checkout_started` or
 *  `iap_purchase_started`, whose `from` names the paywall. */
type MediEvents = {
  medi_shown: { surface: MediSurface };
  medi_dismissed: { surface: MediSurface };
  medi_beat_viewed: { beat: number; total: number };
  medi_show_line_tapped: { beat: number };
  /** The tip folded to one line; it unfolds at the next beat. */
  medi_tip_collapsed: { beat: number };
  /** The leave dialog (back control or Escape). */
  medi_leave_dialog: {
    beat: number;
    action: "opened" | "kept_going" | "left";
  };
  /** `stale` says whether Start over led. */
  medi_practice_action: {
    action: (typeof MEDI_PRACTICE_ACTIONS)[number];
    stale: boolean;
  };
  medi_paywall_shown: { kind: MediPaywallKind };
  medi_paywall_action: {
    kind: MediPaywallKind;
    action: (typeof MEDI_PAYWALL_ACTIONS)[number];
  };
};

export function trackMedi<K extends keyof MediEvents>(
  event: K,
  props: MediEvents[K],
): void {
  if (!isPostHogCapturing()) return;
  posthog.capture(event, props);
}
